import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { XIcon } from './Icons';

interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error?: string;
  helperText?: string;
  showClearButton?: boolean;
  showCharacterCount?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      error,
      helperText,
      showClearButton = false,
      showCharacterCount = false,
      value,
      onChange,
      disabled,
      readOnly,
      maxLength,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const { colors } = useTheme();

    const hasValue = value !== undefined && value !== null && String(value).length > 0;
    const characterCount = value != null ? String(value).length : 0;
    const isOverLimit = maxLength != null && characterCount > maxLength;

    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (disabled || readOnly || !onChange) return;
      const syntheticEvent = {
        target: { value: '', name: props.name },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      onChange(syntheticEvent);
    };

    return (
      <div className="relative">
        <div className="relative">
          <textarea
            ref={ref}
            value={value}
            onChange={onChange}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            rows={rows}
            className={cn(
              'w-full min-h-[120px] border rounded-lg px-3 py-2 text-sm transition-colors resize-y',
              'placeholder:opacity-60 focus:outline-none focus:ring-2 focus:ring-offset-0',
              colors.surface,
              colors.text,
              colors.border,
              colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
              error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
              disabled && 'opacity-50 cursor-not-allowed',
              readOnly && cn('cursor-default', colors.surfaceHover),
              isOverLimit && 'border-red-500',
              showClearButton && hasValue && !disabled && !readOnly && 'pr-10',
              className
            )}
            {...props}
          />
          {showClearButton && hasValue && !disabled && !readOnly && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'absolute top-2 right-2 p-1.5 rounded-md transition-colors',
                colors.textMuted,
                colors.interactiveHover,
                'focus:outline-none focus:ring-2 focus:ring-offset-0',
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : ''
              )}
              title="Clear"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="mt-1 flex justify-between items-start">
          <div className="flex-1">
            {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
            {!error && helperText && <div className={cn('text-sm', colors.textMuted)}>{helperText}</div>}
          </div>
          {showCharacterCount && maxLength != null && (
            <div
              className={cn(
                'text-xs ml-2',
                isOverLimit ? colors.error : colors.textMuted
              )}
            >
              {characterCount}/{maxLength}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
